import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { useCustomerAuth } from '../../context/CustomerAuthContext';
import { getProducts, resolveImageUrl } from '../../services/api';
import WhatsAppAuthModal from '../shared/WhatsAppAuthModal';
import CustomerProfile from '../shared/CustomerProfile';
import {
  MagnifyingGlassIcon, ShoppingCartIcon, AdjustmentsHorizontalIcon,
  XMarkIcon, UserIcon, ShoppingBagIcon, ArrowLeftIcon, Bars3Icon,
  UserCircleIcon, ArrowRightOnRectangleIcon, FunnelIcon,
  ChevronDownIcon, CheckIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const EMOJI_MAP = { Milk:'🥛', Curd:'🥛', Paneer:'🧀', Ghee:'🫙', Butter:'🧈', Buttermilk:'🧃' };
const SORT_OPTIONS = [
  { value:'default',    label:'Default'         },
  { value:'price_asc',  label:'Price: Low → High'},
  { value:'price_desc', label:'Price: High → Low'},
  { value:'name_asc',   label:'Name: A → Z'     },
];

/* ── Profile Dropdown ─────────────────────────────────────────────────────── */
const ProfileDropdown = ({ user, onLogout, onViewProfile }) => (
  <>
    <style>{`
      @keyframes pdIn2{from{opacity:0;transform:translateY(-8px)}to{opacity:1;transform:translateY(0)}}
      .pd2{position:absolute;top:calc(100% + 10px);right:0;background:white;border-radius:1.25rem;
           padding:.4rem;box-shadow:0 16px 40px rgba(0,0,0,.14);border:1px solid #f3f4f6;
           min-width:210px;z-index:200;animation:pdIn2 .2s ease-out;}
      .pd2-hd{padding:.75rem 1rem;border-bottom:1px solid #f3f4f6;margin-bottom:.25rem;}
      .pd2-av{width:2.5rem;height:2.5rem;background:linear-gradient(135deg,#10b981,#059669);
              border-radius:50%;display:flex;align-items:center;justify-content:center;
              color:white;font-size:1rem;font-weight:800;margin-bottom:.4rem;}
      .pd2-nm{font-weight:800;font-size:.92rem;color:#111827;}
      .pd2-ph{font-size:.72rem;color:#6b7280;margin-top:.1rem;}
      .pd2-it{display:flex;align-items:center;gap:.55rem;padding:.55rem .9rem;border-radius:.65rem;
              font-size:.85rem;font-weight:600;color:#374151;cursor:pointer;transition:background .18s;
              border:none;background:none;width:100%;font-family:'Nunito',sans-serif;}
      .pd2-it:hover{background:#f0fdf4;color:#059669;}
      .pd2-it.dng:hover{background:#fff1f2;color:#ef4444;}
      .pd2-it svg{width:.95rem;height:.95rem;flex-shrink:0;}
    `}</style>
    <div className="pd2">
      <div className="pd2-hd">
        <div className="pd2-av">{(user.name||'U').charAt(0).toUpperCase()}</div>
        <div className="pd2-nm">{user.name||'Customer'}</div>
        <div className="pd2-ph">📱 +{user.phone}</div>
      </div>
      <button className="pd2-it" onClick={onViewProfile}><ShoppingBagIcon/>My Orders</button>
      <button className="pd2-it" onClick={onViewProfile}><UserCircleIcon/>My Profile</button>
      <button className="pd2-it dng" onClick={onLogout}><ArrowRightOnRectangleIcon/>Logout</button>
    </div>
  </>
);

/* ── Main Products component ─────────────────────────────────────────────────*/
const Products = () => {
  const navigate  = useNavigate();
  const { addToCart: ctxAddToCart, cartCount } = useCart();
  const { customer: currentUser, isLoggedIn, logout } = useCustomerAuth();

  const [allProducts,    setAllProducts]    = useState([]);
  const [displayed,      setDisplayed]      = useState([]);
  const [categories,     setCategories]     = useState([]);
  const [activeCategory, setActiveCategory] = useState('All');
  const [search,         setSearch]         = useState('');
  const [sort,           setSort]           = useState('default');
  const [loading,        setLoading]        = useState(true);
  const [loadError,      setLoadError]      = useState(false);
  const [addedMap,       setAddedMap]       = useState({});
  const [imgErrors,      setImgErrors]      = useState({});
  const [showFilter,     setShowFilter]     = useState(false);
  const [showSortDrop,   setShowSortDrop]   = useState(false);
  const [showAuthModal,  setShowAuthModal]  = useState(false);
  const [showProfile,    setShowProfile]    = useState(false);
  const [showProfileDrop,setShowProfileDrop]= useState(false);
  const [scrolled,       setScrolled]       = useState(false);

  const searchRef   = useRef(null);
  const sortDropRef = useRef(null);

  // Load products
  useEffect(() => {
    let cancelled = false;
    setLoading(true); setLoadError(false);
    getProducts().then(r => {
      if (cancelled) return;
      const data = r?.data?.products;
      if (Array.isArray(data) && data.length > 0) {
        const mapped = data.map(p => ({
          ...p,
          priceValue:   Number(p.price),
          displayPrice: `₹${Number(p.price).toFixed(2)}`,
        }));
        setAllProducts(mapped);
        const cats = ['All', ...new Set(mapped.map(p => p.category).filter(Boolean))];
        setCategories(cats);
      }
      setLoading(false);
    }).catch(() => {
      if (!cancelled) { setLoadError(true); setLoading(false); }
    });
    return () => { cancelled = true; };
  }, []);

  // Filter + sort
  useEffect(() => {
    let result = [...allProducts];
    if (activeCategory !== 'All') result = result.filter(p => p.category === activeCategory);
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(p =>
        p.name?.toLowerCase().includes(q) || p.category?.toLowerCase().includes(q) || p.description?.toLowerCase().includes(q)
      );
    }
    if (sort === 'price_asc')  result.sort((a,b) => a.priceValue - b.priceValue);
    if (sort === 'price_desc') result.sort((a,b) => b.priceValue - a.priceValue);
    if (sort === 'name_asc')   result.sort((a,b) => (a.name||'').localeCompare(b.name||''));
    setDisplayed(result);
  }, [allProducts, activeCategory, search, sort]);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', fn);
    return () => window.removeEventListener('scroll', fn);
  }, []);

  // Close sort dropdown on outside click
  useEffect(() => {
    if (!showSortDrop) return;
    const fn = () => setShowSortDrop(false);
    setTimeout(() => document.addEventListener('click', fn), 0);
    return () => document.removeEventListener('click', fn);
  }, [showSortDrop]);

  // Close profile dropdown on outside click
  useEffect(() => {
    if (!showProfileDrop) return;
    const fn = () => setShowProfileDrop(false);
    setTimeout(() => document.addEventListener('click', fn), 0);
    return () => document.removeEventListener('click', fn);
  }, [showProfileDrop]);

  const handleAuthSuccess = (user) => {
    setShowAuthModal(false);
    toast.success(`Welcome, ${user.name||'there'}! 🥛`);
  };

  const handleLogout = () => {
    logout();
    setShowProfileDrop(false);
    toast.success('Logged out 👋');
  };

  const handleAddToCart = useCallback((product, e) => {
    if (e) e.stopPropagation();
    ctxAddToCart({ ...product, price: product.priceValue ?? Number(product.price) });
    setAddedMap(prev => ({ ...prev, [product.id]: true }));
    setTimeout(() => setAddedMap(prev => ({ ...prev, [product.id]: false })), 1800);
    toast.success(`${product.name} added! 🛒`);
  }, [ctxAddToCart]);

  const handleBuyNow = useCallback((product, e) => {
    if (e) e.stopPropagation();
    ctxAddToCart({ ...product, price: product.priceValue ?? Number(product.price) });
    navigate('/checkout');
  }, [ctxAddToCart, navigate]);

  const getImg   = (p) => { const raw = p?.image_url||p?.imageUrl||p?.image||null; return resolveImageUrl?resolveImageUrl(raw):raw; };
  const getEmoji = (p) => EMOJI_MAP[p.category] || '🥛';

  const css = `
    @import url('https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700;800;900&display=swap');
    *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
    body{font-family:'Nunito',sans-serif;background:#f9fafb;}
    .pr-hdr{position:sticky;top:0;z-index:50;background:rgba(255,255,255,.97);
            backdrop-filter:blur(12px);border-bottom:1px solid rgba(229,231,235,.6);
            transition:box-shadow .3s;width:100%;}
    .pr-hdr.sc{box-shadow:0 4px 20px rgba(0,0,0,.07);}
    .pr-hdr-in{display:flex;align-items:center;justify-content:space-between;
               height:54px;gap:.4rem;padding:0 .875rem;max-width:1280px;margin:0 auto;}
    @media(min-width:768px){.pr-hdr-in{height:64px;padding:0 1.5rem;}}
    .pr-logo{display:flex;align-items:center;cursor:pointer;flex-shrink:0;}
    .pr-logo img{height:34px;object-fit:contain;}
    @media(min-width:768px){.pr-logo img{height:42px;}}
    .pr-acts{display:flex;align-items:center;gap:.3rem;flex-shrink:0;}
    .pr-ib{width:34px;height:34px;display:flex;align-items:center;justify-content:center;
           border-radius:50%;border:none;background:#f3f4f6;color:#374151;cursor:pointer;
           transition:all .2s;flex-shrink:0;}
    .pr-ib:hover{background:#e5e7eb;color:#059669;}
    .pr-ib svg{width:1rem;height:1rem;}
    @media(min-width:768px){.pr-ib{width:38px;height:38px;}}
    .pr-cw{position:relative;display:inline-flex;}
    .pr-cb{position:absolute;top:-4px;right:-4px;width:.9rem;height:.9rem;background:#ef4444;
           color:white;font-size:.5rem;font-weight:800;border-radius:50%;
           display:flex;align-items:center;justify-content:center;border:2px solid white;}
    .pr-pw{position:relative;display:inline-flex;}
    .pr-pb{display:flex;align-items:center;gap:.3rem;background:#ecfdf5;
           border:1.5px solid #a7f3d0;color:#047857;font-weight:700;font-size:.75rem;
           padding:.28rem .65rem .28rem .28rem;border-radius:9999px;cursor:pointer;
           transition:all .2s;font-family:'Nunito',sans-serif;white-space:nowrap;}
    .pr-pb:hover{background:#d1fae5;}
    .pr-pav{width:1.5rem;height:1.5rem;background:linear-gradient(135deg,#10b981,#059669);
            border-radius:50%;display:flex;align-items:center;justify-content:center;
            color:white;font-size:.65rem;font-weight:800;flex-shrink:0;}
    .pr-pnm{display:none;max-width:65px;overflow:hidden;text-overflow:ellipsis;}
    @media(min-width:440px){.pr-pnm{display:block;}}
    .pr-lbtn{display:flex;align-items:center;gap:.28rem;background:white;color:#059669;
             border:1.5px solid #d1fae5;font-weight:700;font-size:.72rem;
             padding:.32rem .65rem;border-radius:9999px;cursor:pointer;
             transition:all .2s;white-space:nowrap;font-family:'Nunito',sans-serif;}
    .pr-lbtn:hover{background:#f0fdf4;}
    .pr-lbtn svg{width:.75rem;height:.75rem;}
    .pr-main{max-width:1280px;margin:0 auto;padding:.875rem;}
    @media(min-width:768px){.pr-main{padding:1.5rem;}}
    .pr-top{display:flex;align-items:center;gap:.5rem;margin-bottom:1.1rem;}
    .pr-bk{display:flex;align-items:center;gap:.3rem;background:none;border:none;
           color:#6b7280;font-weight:600;font-size:.82rem;cursor:pointer;
           font-family:'Nunito',sans-serif;padding:0;transition:color .2s;flex-shrink:0;}
    .pr-bk:hover{color:#059669;}
    .pr-bk svg{width:.9rem;height:.9rem;}
    .pr-ttl{font-size:1.1rem;font-weight:900;color:#111827;}
    @media(min-width:768px){.pr-ttl{font-size:1.35rem;}}
    .pr-cnt{font-size:.78rem;color:#6b7280;font-weight:600;margin-left:auto;white-space:nowrap;}
    .pr-sb{display:flex;align-items:center;gap:.5rem;background:white;
           border:2px solid #e5e7eb;border-radius:.85rem;padding:.55rem .9rem;
           margin-bottom:1rem;transition:border-color .2s;}
    .pr-sb:focus-within{border-color:#10b981;box-shadow:0 0 0 3px rgba(16,185,129,.1);}
    .pr-sb svg{width:1rem;height:1rem;color:#9ca3af;flex-shrink:0;}
    .pr-si{flex:1;border:none;outline:none;font-size:.92rem;background:transparent;
           font-family:'Nunito',sans-serif;font-weight:600;min-width:0;}
    .pr-si::placeholder{color:#9ca3af;font-weight:400;}
    .pr-toolbar{display:flex;align-items:center;gap:.5rem;margin-bottom:1.1rem;flex-wrap:wrap;}
    .pr-cats{display:flex;gap:.4rem;overflow-x:auto;flex:1;min-width:0;
             scrollbar-width:none;-ms-overflow-style:none;}
    .pr-cats::-webkit-scrollbar{display:none;}
    .pr-cat{padding:.35rem .85rem;border-radius:9999px;border:1.5px solid #e5e7eb;
            font-weight:700;font-size:.78rem;cursor:pointer;transition:all .2s;
            white-space:nowrap;font-family:'Nunito',sans-serif;background:white;color:#374151;flex-shrink:0;}
    .pr-cat.act{background:#059669;color:white;border-color:#059669;}
    .pr-cat:hover:not(.act){border-color:#10b981;color:#059669;}
    .pr-sort{position:relative;flex-shrink:0;}
    .pr-sortbtn{display:flex;align-items:center;gap:.3rem;background:white;
                border:1.5px solid #e5e7eb;color:#374151;font-weight:700;font-size:.78rem;
                padding:.38rem .8rem;border-radius:9999px;cursor:pointer;
                transition:all .2s;font-family:'Nunito',sans-serif;white-space:nowrap;}
    .pr-sortbtn:hover{border-color:#10b981;color:#059669;}
    .pr-sortbtn svg{width:.82rem;height:.82rem;}
    .pr-sortdrop{position:absolute;top:calc(100% + 6px);right:0;background:white;
                 border-radius:1rem;padding:.3rem;box-shadow:0 12px 30px rgba(0,0,0,.12);
                 border:1px solid #f3f4f6;min-width:175px;z-index:100;}
    .pr-sortopt{display:flex;align-items:center;justify-content:space-between;
                padding:.5rem .85rem;border-radius:.6rem;font-size:.83rem;font-weight:600;
                color:#374151;cursor:pointer;background:none;border:none;width:100%;
                font-family:'Nunito',sans-serif;gap:.5rem;}
    .pr-sortopt:hover{background:#f0fdf4;color:#059669;}
    .pr-sortopt.act{color:#059669;font-weight:800;}
    .pr-sortopt svg{width:.82rem;height:.82rem;color:#059669;flex-shrink:0;}
    .pr-grid{display:grid;grid-template-columns:repeat(2,1fr);gap:1rem;}
    @media(min-width:520px){.pr-grid{grid-template-columns:repeat(3,1fr);gap:1.1rem;}}
    @media(min-width:768px){.pr-grid{grid-template-columns:repeat(4,1fr);gap:1.25rem;}}
    @media(min-width:1024px){.pr-grid{grid-template-columns:repeat(5,1fr);gap:1.5rem;}}
    @media(min-width:1280px){.pr-grid{grid-template-columns:repeat(6,1fr);}}
    .pr-card{background:white;border-radius:1.1rem;border:1.5px solid #f3f4f6;
             overflow:hidden;transition:all .28s;cursor:pointer;display:flex;
             flex-direction:column;}
    .pr-card:hover{transform:translateY(-4px);box-shadow:0 16px 28px -6px rgba(5,150,105,.12);
                   border-color:rgba(5,150,105,.25);}
    .pr-img-w{position:relative;width:100%;aspect-ratio:1/1;background:#f9fafb;
              display:flex;align-items:center;justify-content:center;overflow:hidden;}
    .pr-img{width:100%;height:100%;object-fit:contain;display:block;transition:transform .35s;
            filter:drop-shadow(0 4px 10px rgba(0,0,0,.08));}
    .pr-card:hover .pr-img{transform:scale(1.07);}
    .pr-emoji{font-size:3.5rem;line-height:1;}
    .pr-badge{position:absolute;top:.45rem;left:.45rem;background:#f59e0b;color:white;
              font-size:.52rem;font-weight:700;padding:.14rem .48rem;border-radius:9999px;z-index:1;}
    .pr-oos{position:absolute;inset:0;background:rgba(255,255,255,.75);
            display:flex;align-items:center;justify-content:center;
            font-size:.72rem;font-weight:800;color:#6b7280;z-index:2;}
    .pr-info{padding:.65rem .7rem .7rem;flex:1;display:flex;flex-direction:column;}
    .pr-cat2{font-size:.65rem;font-weight:700;color:#059669;text-transform:uppercase;
             letter-spacing:.3px;margin-bottom:.18rem;}
    .pr-name{font-size:.85rem;font-weight:800;color:#111827;margin-bottom:.22rem;
             white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
    .pr-desc{font-size:.72rem;color:#9ca3af;line-height:1.4;margin-bottom:.5rem;
             display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;}
    .pr-price{font-size:1rem;font-weight:900;color:#111827;margin-bottom:.55rem;margin-top:auto;}
    .pr-btns{display:grid;grid-template-columns:1fr 1fr;gap:.3rem;}
    .pr-add{padding:.4rem .1rem;background:#f0fdf4;color:#059669;font-weight:700;
            font-size:.65rem;border:1.5px solid #d1fae5;border-radius:.5rem;
            cursor:pointer;transition:all .2s;font-family:'Nunito',sans-serif;text-align:center;}
    .pr-add:hover,.pr-add.done{background:#059669;color:white;border-color:#059669;}
    .pr-buy{padding:.4rem .1rem;background:linear-gradient(135deg,#10b981,#059669);
            color:white;font-weight:700;font-size:.65rem;border:none;
            border-radius:.5rem;cursor:pointer;transition:all .2s;
            font-family:'Nunito',sans-serif;text-align:center;}
    .pr-buy:hover{box-shadow:0 4px 8px rgba(5,150,105,.3);}
    .pr-oos-btn{grid-column:1/-1;padding:.4rem;background:#f3f4f6;color:#9ca3af;
                font-weight:700;font-size:.65rem;border:none;border-radius:.5rem;
                cursor:not-allowed;font-family:'Nunito',sans-serif;text-align:center;}
    @keyframes shimmer{0%,100%{opacity:1}50%{opacity:.35}}
    .sk{animation:shimmer 1.4s ease-in-out infinite;background:#f3f4f6;border-radius:.6rem;}
    .pr-empty{grid-column:1/-1;text-align:center;padding:3rem 1rem;color:#9ca3af;}
    .pr-empty-ico{font-size:3rem;margin-bottom:.75rem;}
    .pr-empty h3{font-size:1rem;font-weight:800;color:#374151;margin-bottom:.35rem;}
    .pr-empty p{font-size:.85rem;}
  `;

  const SkeletonCard = () => (
    <div style={{background:'white',borderRadius:'1.1rem',overflow:'hidden',border:'1.5px solid #f3f4f6'}}>
      <div className="sk" style={{width:'100%',aspectRatio:'1/1'}}/>
      <div style={{padding:'.65rem .7rem'}}>
        <div className="sk" style={{height:'.55rem',width:'55%',marginBottom:'.35rem'}}/>
        <div className="sk" style={{height:'.7rem',width:'88%',marginBottom:'.35rem'}}/>
        <div className="sk" style={{height:'.65rem',width:'40%',marginBottom:'.55rem'}}/>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'.3rem'}}>
          <div className="sk" style={{height:'1.5rem',borderRadius:'.5rem'}}/>
          <div className="sk" style={{height:'1.5rem',borderRadius:'.5rem'}}/>
        </div>
      </div>
    </div>
  );

  const ProductCard = ({ p }) => {
    const imgSrc  = getImg(p);
    const inStock = p.stock_quantity == null || p.stock_quantity > 0;
    const added   = addedMap[p.id];
    return (
      <div className="pr-card" onClick={()=>{}}>
        <div className="pr-img-w">
          {p.is_featured && <span className="pr-badge">⭐ Featured</span>}
          {imgSrc && !imgErrors[p.id]
            ? <img className="pr-img" src={imgSrc} alt={p.name} onError={()=>setImgErrors(prev=>({...prev,[p.id]:true}))}/>
            : <span className="pr-emoji">{getEmoji(p)}</span>}
          {!inStock && <div className="pr-oos">Out of Stock</div>}
        </div>
        <div className="pr-info">
          {p.category && <div className="pr-cat2">{p.category}</div>}
          <div className="pr-name" title={p.name}>{p.name}</div>
          {p.description && <div className="pr-desc">{p.description}</div>}
          <div className="pr-price">{p.displayPrice || `₹${p.price}`}</div>
          <div className="pr-btns">
            {inStock ? (
              <>
                <button className={`pr-add${added?' done':''}`} onClick={e=>handleAddToCart(p,e)}>
                  {added ? '✓ Added' : '+ Cart'}
                </button>
                <button className="pr-buy" onClick={e=>handleBuyNow(p,e)}>Buy Now</button>
              </>
            ) : (
              <button className="pr-oos-btn" disabled>Out of Stock</button>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <style>{css}</style>
      {showAuthModal && <WhatsAppAuthModal onSuccess={handleAuthSuccess} onClose={()=>setShowAuthModal(false)}/>}
      {showProfile   && <CustomerProfile  onClose={()=>setShowProfile(false)}/>}

      {/* HEADER */}
      <header className={`pr-hdr${scrolled?' sc':''}`}>
        <div className="pr-hdr-in">
          <div className="pr-logo" onClick={()=>navigate('/')}>
            <img src="/images/logo.png" alt="Fresh Hills"
              onError={e=>{e.target.onerror=null;e.target.src='https://via.placeholder.com/42?text=FH';}}/>
          </div>
          <div className="pr-acts">
            <div className="pr-cw">
              <button className="pr-ib" onClick={()=>navigate('/cart')}><ShoppingCartIcon/></button>
              {cartCount>0 && <span className="pr-cb">{cartCount>9?'9+':cartCount}</span>}
            </div>
            {isLoggedIn ? (
              <div className="pr-pw" onClick={e=>{e.stopPropagation();setShowProfileDrop(p=>!p);}}>
                <button className="pr-pb">
                  <div className="pr-pav">{(currentUser.name||'U').charAt(0).toUpperCase()}</div>
                  <span className="pr-pnm">{(currentUser.name||'').split(' ')[0]||currentUser.phone}</span>
                </button>
                {showProfileDrop && (
                  <ProfileDropdown
                    user={currentUser}
                    onLogout={handleLogout}
                    onViewProfile={()=>{setShowProfileDrop(false);setShowProfile(true);}}
                  />
                )}
              </div>
            ) : (
              <button className="pr-lbtn" onClick={()=>setShowAuthModal(true)}>
                <UserIcon/>Login
              </button>
            )}
          </div>
        </div>
      </header>

      {/* MAIN */}
      <main className="pr-main">
        {/* Top bar */}
        <div className="pr-top">
          <button className="pr-bk" onClick={()=>navigate('/')}>
            <ArrowLeftIcon/>Home
          </button>
          <h1 className="pr-ttl">🛒 All Products</h1>
          {!loading && (
            <span className="pr-cnt">{displayed.length} of {allProducts.length}</span>
          )}
        </div>

        {/* Search */}
        <div className="pr-sb">
          <MagnifyingGlassIcon/>
          <input ref={searchRef} className="pr-si" placeholder="Search milk, paneer, curd…"
            value={search} onChange={e=>setSearch(e.target.value)}/>
          {search && (
            <button style={{background:'none',border:'none',cursor:'pointer',color:'#9ca3af',
                            display:'flex',alignItems:'center',padding:0}}
              onClick={()=>{setSearch('');searchRef.current?.focus();}}>
              <XMarkIcon style={{width:'1rem',height:'1rem'}}/>
            </button>
          )}
        </div>

        {/* Category + Sort toolbar */}
        <div className="pr-toolbar">
          <div className="pr-cats">
            {categories.map(cat => (
              <button key={cat} className={`pr-cat${activeCategory===cat?' act':''}`}
                onClick={()=>setActiveCategory(cat)}>
                {cat==='All'?'🛍️ All':cat}
              </button>
            ))}
          </div>
          <div className="pr-sort" ref={sortDropRef}>
            <button className="pr-sortbtn" onClick={e=>{e.stopPropagation();setShowSortDrop(p=>!p);}}>
              <FunnelIcon/>{SORT_OPTIONS.find(o=>o.value===sort)?.label||'Sort'}<ChevronDownIcon/>
            </button>
            {showSortDrop && (
              <div className="pr-sortdrop" onClick={e=>e.stopPropagation()}>
                {SORT_OPTIONS.map(o=>(
                  <button key={o.value} className={`pr-sortopt${sort===o.value?' act':''}`}
                    onClick={()=>{setSort(o.value);setShowSortDrop(false);}}>
                    {o.label}
                    {sort===o.value && <CheckIcon/>}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Grid */}
        <div className="pr-grid">
          {loading
            ? Array.from({length:12}).map((_,i)=><SkeletonCard key={i}/>)
            : loadError
              ? <div className="pr-empty">
                  <div className="pr-empty-ico">⚠️</div>
                  <h3>Could not load products</h3>
                  <p>Check your connection and try again.</p>
                  <button style={{marginTop:'1rem',background:'#059669',color:'white',
                                  border:'none',borderRadius:'9999px',padding:'.6rem 1.4rem',
                                  fontWeight:'700',cursor:'pointer',fontFamily:'Nunito,sans-serif'}}
                    onClick={()=>window.location.reload()}>Retry</button>
                </div>
              : displayed.length === 0
                ? <div className="pr-empty">
                    <div className="pr-empty-ico">🔍</div>
                    <h3>No products found</h3>
                    <p>Try a different search or category.</p>
                  </div>
                : displayed.map(p => <ProductCard key={p.id} p={p}/>)}
        </div>
      </main>
    </>
  );
};

export default Products;